import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'
import { invoicesKey, unwrap, useFreshCompanyId } from './invoice-api-helpers'
import type {
  CustomerOpenDocuments,
  Invoice,
  InvoiceBody,
  InvoiceTotalsPreview,
  InvoiceUpdateBody,
  ReceiveInvoicePaymentBody,
} from './invoice-api-types'

export {
  useApplyCreditNote,
  useApplyPaymentCredit,
  useCreateCreditNote,
  usePreviewCreditNoteTotals,
  useUndoCreditApplication,
} from './invoice-credit-note-mutations'

export function useCreateInvoice(
  companyId: string | null,
  options?: Omit<UseMutationOptions<Invoice, Error, InvoiceBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Invoice, Error, InvoiceBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/invoices', {
        headers: { 'X-Company-Id': currentCompany() },
        body: body as components['schemas']['StoreInvoiceRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create invoice')
      return unwrap<Invoice>(response.data ?? {}, 'invoices')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useUpdateInvoice(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<Invoice, Error, { invoiceId: string; body: InvoiceUpdateBody }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Invoice, Error, { invoiceId: string; body: InvoiceUpdateBody }>({
    ...options,
    mutationFn: async ({ invoiceId, body }) => {
      const response = await api.PATCH('/accounting/invoices/{invoice}', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { invoice: invoiceId } },
        body: body as components['schemas']['UpdateInvoiceRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to update invoice')
      return unwrap<Invoice>(response.data ?? {}, 'invoices')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function usePreviewInvoiceTotals(companyId: string | null) {
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<
    InvoiceTotalsPreview,
    Error,
    { invoiceId?: string; body: InvoiceBody | InvoiceUpdateBody }
  >({
    mutationFn: async ({ invoiceId, body }) => {
      const headers = { 'X-Company-Id': currentCompany() }
      const response = invoiceId
        ? await api.POST('/accounting/invoices/{invoice}/totals-preview', {
            headers,
            params: { path: { invoice: invoiceId } },
            body: body as components['schemas']['UpdateInvoiceTotalsPreviewRequest'],
          })
        : await api.POST('/accounting/invoices/totals-preview', {
            headers,
            body: body as components['schemas']['StoreInvoiceTotalsPreviewRequest'],
          })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to preview invoice totals')
      }
      return unwrap<InvoiceTotalsPreview>(response.data ?? {}, 'invoice totals preview')
    },
  })
}

export function useConfirmInvoice(
  companyId: string | null,
  options?: Omit<UseMutationOptions<Invoice, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Invoice, Error, string>({
    ...options,
    mutationFn: async (invoiceId) => {
      const response = await api.POST('/accounting/invoices/{invoice}/confirm', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { invoice: invoiceId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to confirm invoice')
      return unwrap<Invoice>(response.data ?? {}, 'invoices')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function useDeleteDraftInvoice(
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<void, Error, string>({
    ...options,
    mutationFn: async (invoiceId) => {
      const response = await api.DELETE('/accounting/invoices/{invoice}', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { invoice: invoiceId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to delete invoice')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function customerOpenDocumentsKey(companyId: string | null, customerId: string | null) {
  return ['companies', companyId, 'accounting', 'customers', customerId, 'open-documents'] as const
}

export function useCustomerOpenDocuments(
  companyId: string | null,
  customerId: string | null,
  options?: Omit<UseQueryOptions<CustomerOpenDocuments, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CustomerOpenDocuments, Error>({
    queryKey: customerOpenDocumentsKey(companyId, customerId),
    enabled: Boolean(companyId && customerId),
    ...options,
    queryFn: async () => {
      if (!companyId || !customerId) throw new Error('Company and customer required')
      const response = await api.GET('/accounting/customers/{customer}/open-documents', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { customer: customerId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to load customer open documents')
      }
      return response.data?.data as unknown as CustomerOpenDocuments
    },
  })
}

export function useReceiveInvoicePayment(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<
      components['schemas']['PaymentResource'],
      Error,
      { invoiceId: string; body: ReceiveInvoicePaymentBody }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<
    components['schemas']['PaymentResource'],
    Error,
    { invoiceId: string; body: ReceiveInvoicePaymentBody }
  >({
    ...options,
    mutationFn: async ({ invoiceId, body }) => {
      const response = await api.POST('/accounting/invoices/{invoice}/payments', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { invoice: invoiceId } },
        body,
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to record payment')
      return response.data?.data as components['schemas']['PaymentResource']
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'customers'],
      })
      if (args[0].direction === 'inbound' && args[0].deposit_to_account_id) {
        void queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'accounting', 'undeposited-receipts'],
        })
      }
      options?.onSuccess?.(...args)
    },
  })
}
